import { useEffect, useState } from "react";
import "../../../Account/OrderHistory/styles/OrderHistory.css";
import { ChevronRight } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { fetchTopPicks } from "../../../../features/products/topPicks/redux/topPicksSlice";
import blankcart from "../../../../assets/images/empty-order-history.png";
import rightarrawwhite from "../../../../assets/icons/rightarrawwhite.png";
import { formatFullDate, formatShortDate } from "../../../../utils/date.utils";
import { formatPrice } from "../../../../utils/price.utils";
import { limitChars } from "../../../../utils/text.utils";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { fetchOrderHistory } from "../redux/orderHistorySlice";
import { getProductPdpUrl } from "../../../../utils/url.utils";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const STATUS_OPTIONS = [
  { key: "", label: "All Orders" },
  { key: "ORDER_PLACED", label: "Order Placed" },
  { key: "DELIVERED", label: "Delivered" },
  { key: "CANCELLED", label: "Cancelled" },
  { key: "RETURNED", label: "Returned" },
];

const STATUS_CONFIG = {
  "Order Placed": { boxClass: "Order-placed", indicatorClass: "orange" },
  Shipped: { boxClass: "Delivery-box", indicatorClass: "orangebg" },
  Delivered: { boxClass: "Delivery-box", indicatorClass: "greenbg" },
  "Order Cancelled": { boxClass: "Refund-box", indicatorClass: "red" },
  Refunded: { boxClass: "Refund-box", indicatorClass: "red" },
};

// Skeletons
const SidebarSkeleton = () => (
  <aside className="sidebar">
    <div className="filters Filter-box">
      <Skeleton width={80} height={20} />
      <Skeleton width={120} height={15} style={{ marginTop: 10 }} />
      {[...Array(5)].map((_, i) => (
        <div key={i} style={{ marginTop: 10 }}>
          <Skeleton width={150} height={15} />
        </div>
      ))}
    </div>
  </aside>
);

const SearchSkeleton = () => (
  <div className="searchbox">
    <Skeleton height={45} />
  </div>
);

const OrderCardSkeleton = () => (
  <div className="order-card">
    <div className="order-header">
      <Skeleton width={120} height={15} />
      <Skeleton width={100} height={15} />
    </div>
    <div className="order-item" style={{ display: "flex", gap: 15 }}>
      <Skeleton width={80} height={80} />
      <div style={{ flex: 1 }}>
        <Skeleton width="80%" height={15} />
        <Skeleton width="40%" height={15} style={{ marginTop: 8 }} />
        <Skeleton width="30%" height={15} style={{ marginTop: 8 }} />
      </div>
    </div>
  </div>
);

const OrderHistoryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const { results = [], loading, error } = useSelector((state) => state.orders);

  // On mount: read URL params and fetch orders
  useEffect(() => {
    document.title = "Obsessions - Order History";
    const params = new URLSearchParams(window.location.search);
    const statusFromUrl = params.get("status") || "";
    const orderNoFromUrl = params.get("order_no") || "";

    setSelectedStatus(statusFromUrl);
    setSearchValue(orderNoFromUrl);
    setSearchQuery(orderNoFromUrl);
    setCurrentPage(1);

    dispatch(fetchTopPicks());
    dispatch(
      fetchOrderHistory({
        status: statusFromUrl,
        page: 1,
        limit: 10,
        order_no: orderNoFromUrl,
      })
    );
  }, [dispatch]);

  // Fetch orders when filters/search/page changes
  useEffect(() => {
    dispatch(
      fetchOrderHistory({
        status: selectedStatus || "",
        page: currentPage,
        limit: 10,
        order_no: searchQuery || "",
      })
    );
  }, [dispatch, selectedStatus, currentPage, searchQuery]);

  // Reset all orders when search input is cleared
  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
      setSearchQuery("");
      dispatch(
        fetchOrderHistory({
          status: selectedStatus || "",
          page: 1,
          limit: 10,
          order_no: "",
        })
      );
      const params = new URLSearchParams(window.location.search);
      if (selectedStatus) params.set("status", selectedStatus);
      else params.delete("status");
      params.delete("order_no");
      window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    }
  }, [searchValue, dispatch, selectedStatus]);

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = searchValue.trim();
    setCurrentPage(1);
    setSearchQuery(trimmed);

    const params = new URLSearchParams(window.location.search);
    if (trimmed) params.set("order_no", trimmed);
    else params.delete("order_no");

    if (selectedStatus) params.set("status", selectedStatus);
    else params.delete("status");

    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  };

  // Handle status change
  const handleStatusChange = (statusKey) => {
    const newStatus = selectedStatus === statusKey ? "" : statusKey;
    setSelectedStatus(newStatus);
    setCurrentPage(1);

    const params = new URLSearchParams(window.location.search);
    if (newStatus) params.set("status", newStatus);
    else params.delete("status");

    if (searchQuery) params.set("order_no", searchQuery);
    else params.delete("order_no");

    window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  };

  if (loading) {
    return (
      <div className="order-history">
        <SidebarSkeleton />
        <main className="order-list">
          <SearchSkeleton />
          {[...Array(5)].map((_, i) => (
            <OrderCardSkeleton key={i} />
          ))}
        </main>
      </div>
    );
  }

  if (error) return <p style={{ color: "red" }}>Error loading orders</p>;

  return (
    <div className="order-history">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="filters Filter-box">
          <h4>Filters</h4>
          <p>ORDER STATUS</p>
          {STATUS_OPTIONS.map((status) => (
            <label key={status.key}>
              <input
                type="checkbox"
                checked={selectedStatus === status.key}
                onChange={() => handleStatusChange(status.key)}
              />
              <span className="status_key">{status.label}</span>
            </label>
          ))}
        </div>
      </aside>

      {/* Empty State */}
      {results.length === 0 ? (
        <div className="empty-orderhistory">
          <img src={blankcart} alt="Empty cart" />
          <h3>You haven't placed any orders yet.</h3>
          <button onClick={() => navigate("/cart")} className="empty-cart-btn">
            EXPLORE <img src={rightarrawwhite} alt="" width={20} />
          </button>
        </div>
      ) : (
        <main className="order-list">
          {/* Search */}
          <form onSubmit={handleSearch}>
            <div className="searchbox">
              <div className="filtericon">
                <button
                  type="button"
                  className="filter-btn"
                  onClick={() => setShowFilterModal(true)}
                >
                  <i className="bi bi-filter"></i>
                </button>
              </div>

              <div className="search-inputbox">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search your order here"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>

              <div className="searcbuttonicon">
                <button type="submit" className="serch-btns">
                  <span>
                    <i className="bi bi-search"></i>
                  </span>
                  <span> Search </span>
                </button>
              </div>
            </div>
          </form>

          {/* Orders List */}
          {results.map((order) => {
            const firstItem = order?.order_items?.[0];
            const extraCount = (order?.order_items?.length || 0) - 1;
            const status = order?.order_status?.remarks;
            const currentStatus =
              STATUS_CONFIG[status] || STATUS_CONFIG["Order Placed"];

            return (
              <div key={order.order_no} className="order-card">
                <div className="root_new">
                  <div className="order-header">
                    <div>
                      <div style={{ fontSize: "12px" }}>Order Placed</div>
                      <div>{formatFullDate(order.order_placed_at)}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: "12px" }}>Order Number</div>
                      <Link
                        to={`/accounts/orders/${order.order_no}`}
                        style={{ textDecoration: "none", color: "#222" }}
                      >
                        {order.order_no}
                      </Link>
                    </div>

                    <div className="order-actions">
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "12px" }}>Invoice</div>
                        <span
                          className="txt_cation"
                          style={{
                            display: "flex",
                            gap: "5px",
                            justifyContent: "right",
                            alignItems: "center",
                          }}
                        >
                          {order?.invoice_url ? (
                            <a
                              href={order.invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "rgb(243, 64, 64)" }}
                            >
                              <i className="bi bi-file-earmark-pdf"></i>
                            </a>
                          ) : (
                            <span
                              style={{
                                opacity: 0.4,
                                cursor: "not-allowed",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              title="Invoice not available"
                            >
                              <i className="bi bi-file-earmark-pdf text-muted"></i>
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {firstItem && (
                    <div className="order-item">
                      <LazyLoadImage
                        src={firstItem.product_media}
                        alt={firstItem.product_name}
                      />
                      <div className="all-item-section">
                        <div className="item-info">
                          <p>
                            <Link
                              to={getProductPdpUrl(firstItem.action_url, firstItem.sku)}
                            >
                              {limitChars(firstItem.product_name, 45)}
                            </Link>
                          </p>

                          <p>
                            {formatPrice(firstItem.selling_price)} | Qty: {order.quantity}
                          </p>

                          {extraCount > 0 && (
                            <p>
                              <Link
                                to={`/accounts/orders/${order.order_no}`}
                                style={{ color: "orange" }}
                              >
                                +{extraCount} more item
                              </Link>
                            </p>
                          )}
                        </div>

                        <div className={currentStatus.boxClass}>
                          <Link
                            to={`/accounts/orders/${order.order_no}`}
                            style={{ color: "#000", textDecoration: "none" }}
                          >
                            <div className={currentStatus.indicatorClass}></div>
                            <span>{status} {formatShortDate(order)}</span>
                          </Link>
                        </div>
                      </div>

                      <Link to={`/accounts/orders/${order.order_no}`}>
                        <ChevronRight size={22} />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </main>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="filter-modal-overlay filetr-Order-history">
          <div className="filter-modal-bottom">
            <div className="filter-modal-header">
              <h4>Filters</h4>
              <button
                className="close-btn"
                onClick={() => setShowFilterModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="filter-modal-body">
              <p>ORDER STATUS</p>
              {STATUS_OPTIONS.map((status) => (
                <label key={status.key} className="filter-option">
                  <input
                    type="checkbox"
                    checked={selectedStatus === status.key}
                    onChange={() => handleStatusChange(status.key)}
                  />
                  <span>{status.label}</span>
                </label>
              ))}
            </div>

            <div className="filter-modal-footer">
              <button
                className="clear-btn"
                onClick={() => {
                  setSelectedStatus("");
                  setSearchValue("");
                  setSearchQuery("");
                  setCurrentPage(1);
                  setShowFilterModal(false);
                  window.history.replaceState({}, "", window.location.pathname);

                  dispatch(
                    fetchOrderHistory({
                      status: "",
                      page: 1,
                      limit: 10,
                      order_no: "",
                    })
                  );
                }}
              >
                Clear All
              </button>

              <button
                className="apply-btn"
                onClick={() => setShowFilterModal(false)}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default OrderHistoryPage;